import { useState, useRef } from "react";
import { B, PD } from "../theme";
import { EntryTeaser, SPOKE_ACCENTS, SPOKE_LABELS as LABELS } from "../modules/entry-teaser";

/* Right after signup (ob-003): one short question from each of the Web's
   five parts, then the Web. Each answer is that spoke's entry question
   (saved the same way as from the wheel) and lights its node on the little
   web up top. Every step can be skipped, one question or all of them. */

export const FIRST_SPOKES = ["archetype", "personality", "values", "behavior", "belief"];

// Same angles as the wheel (SPOKE_ANGLES in PersonalWeb): Archetypes on top,
// then clockwise.
const node = (i, r) => {
  const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
  return [60 + Math.cos(a) * r, 52 + Math.sin(a) * r];
};

export function LittleWeb({ current, lit, justLit }) {
  return (
    <svg width="120" height="104" viewBox="0 0 120 104" role="img"
      aria-label={`Your web: ${lit.length} of 5 parts lit`} style={{ display: "block", margin: "0 auto 10px", overflow: "visible" }}>
      <style>{`@keyframes fqLight{0%{transform:scale(0.4);opacity:0.3}60%{transform:scale(1.5);opacity:1}100%{transform:scale(1);opacity:1}}
@keyframes fqNow{0%,100%{opacity:0.45}50%{opacity:1}}
.fq-lit{transform-box:fill-box;transform-origin:center}
.fq-just{animation:fqLight 0.8s ease-out 0.25s both}
.fq-now{animation:fqNow 2.2s ease-in-out infinite}
@media(prefers-reduced-motion:reduce){.fq-just,.fq-now{animation:none}}`}</style>
      {FIRST_SPOKES.map((k, i) => {
        const [x, y] = node(i, 40);
        const on = lit.includes(k);
        return <line key={`l-${k}`} x1="60" y1="52" x2={x} y2={y} stroke={on ? SPOKE_ACCENTS[k] : PD.thread} strokeWidth={on ? 1.4 : 1} opacity={on ? 0.8 : 0.6} />;
      })}
      <circle cx="60" cy="52" r="3.4" fill={lit.length === 5 ? PD.text : PD.thread} />
      {FIRST_SPOKES.map((k, i) => {
        const [x, y] = node(i, 40);
        const accent = SPOKE_ACCENTS[k];
        const on = lit.includes(k);
        if (on) {
          return (
            <g key={k}>
              <circle cx={x} cy={y} r="10" fill={accent} opacity="0.22" className={`fq-lit${k === justLit ? " fq-just" : ""}`} />
              <circle cx={x} cy={y} r="5.5" fill={accent} className={`fq-lit${k === justLit ? " fq-just" : ""}`} />
            </g>
          );
        }
        if (k === current) {
          return <circle key={k} cx={x} cy={y} r="6.5" fill="none" stroke={accent} strokeWidth="1.6" className="fq-now" />;
        }
        return <circle key={k} cx={x} cy={y} r="4" fill={PD.bg} stroke={PD.thread} strokeWidth="1.2" />;
      })}
    </svg>
  );
}

export function FirstQuestions({ spokes: spokesIn = FIRST_SPOKES, user, onAnswered, onDone }) {
  // Fixed at the start: the caller's list shrinks as answers come in.
  const [spokes] = useState(() => spokesIn);
  const [step, setStep] = useState(0);
  const [finishing, setFinishing] = useState(false);
  // Leave once: a skip during an answer's save beat must not finish twice.
  const left = useRef(false);
  const finish = () => { if (!left.current) { left.current = true; onDone(); } };
  // Parts already started before signup show lit from the start.
  const [lit, setLit] = useState(() => FIRST_SPOKES.filter((k) => !spokesIn.includes(k)));
  const [justLit, setJustLit] = useState(null);
  const spoke = spokes[step];

  const next = () => {
    if (step + 1 >= spokes.length) finish();
    else setStep(step + 1);
  };
  const answered = ({ spoke: k }) => {
    if (left.current) return;
    setLit((l) => (l.includes(k) ? l : [...l, k]));
    setJustLit(k);
    onAnswered?.(k);
    // The last answer holds a beat so its part lights up before the Web.
    if (step + 1 >= spokes.length) { setFinishing(true); setTimeout(finish, 1300); }
    else next();
  };

  if (!spoke) return null;
  if (finishing) {
    return (
      <div style={{ minHeight: "100vh", background: PD.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ transform: "scale(1.6)" }}><LittleWeb current={null} lit={lit} justLit={justLit} /></div>
      </div>
    );
  }
  const accent = SPOKE_ACCENTS[spoke];

  const header = (
    <div>
      <LittleWeb current={spoke} lit={lit} justLit={justLit} />
      <p style={{ ...B, fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", color: accent, margin: "0 0 8px" }}>
        {LABELS[spoke]} · {step + 1} of {spokes.length}
      </p>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      <EntryTeaser
        key={spoke}
        spokeKey={spoke}
        user={user}
        header={header}
        onComplete={answered}
        onSkip={next}
        onBack={step > 0 ? () => setStep(step - 1) : undefined}
        skipLabel={step + 1 >= spokes.length ? "Skip this one and see your Web" : "Skip this one"}
      />
      <button type="button" onClick={finish} style={{
        ...B, position: "absolute", top: 8, right: 8, zIndex: 2, fontSize: 13, color: PD.sub,
        background: "none", border: "none", padding: "12px 14px", minHeight: 44, cursor: "pointer",
      }}>
        Skip to the Web
      </button>
    </div>
  );
}
